import React from 'react';
import { motion, MotionProps } from 'framer-motion';

interface MonitorProps extends MotionProps {
    children?: React.ReactNode;
    className?: string;
    width?: string;
    height?: string;
    borderColor?: string;
    standWidthClass?: string;
    standHeightClass?: string;
    standNeckWidthClass?: string;
    standNeckHeightClass?: string;
    baseWidthClass?: string;
    baseHeightClass?: string;
}

export const Monitor = ({
    children,
    className = "",
    width = "w-36",
    height = "h-24",
    borderColor = "border-gray-600",
    standWidthClass = "w-8",
    standHeightClass = "h-3",
    standNeckWidthClass = "w-6",
    standNeckHeightClass = "h-1",
    baseWidthClass = "w-12",
    baseHeightClass = "h-2",
    initial,
    animate,
    transition,
    ...motionProps
}: MonitorProps) => {
    const defaultInitial = { opacity: 0, scale: 0.9 };
    const defaultAnimate = { opacity: 1, scale: 1 };
    const defaultTransition = { duration: 0.5 };

    const isPlainObject = (value: unknown): value is Record<string, unknown> =>
        typeof value === "object" && value !== null && !Array.isArray(value);

    const resolvedInitial =
        isPlainObject(initial) ? { ...defaultInitial, ...initial } : (initial ?? defaultInitial);

    const resolvedAnimate =
        isPlainObject(animate) ? { ...defaultAnimate, ...animate } : (animate ?? defaultAnimate);

    const resolvedTransition = transition
        ? { ...defaultTransition, ...transition }
        : defaultTransition;

    return (
        <div className={`flex flex-col items-center ${className}`}>
            {/* Monitor screen */}
            <motion.div
                className={`${width} ${height} bg-gray-800 rounded-lg border-2 ${borderColor} relative overflow-hidden mx-auto shadow-lg`}
                initial={resolvedInitial}
                animate={resolvedAnimate}
                transition={resolvedTransition}
                {...motionProps}
            >
                {/* Screen content */}
                <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                    {children}
                </div>

                {/* Screen glare */}
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
            </motion.div>

            {/* Monitor stand */}
            <div className={`${standWidthClass} ${standHeightClass} bg-gray-700 rounded mx-auto relative mt-0`}>
                <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 ${standNeckWidthClass} ${standNeckHeightClass} bg-gray-600 rounded-b`} />
            </div>

            {/* Base */}
            <div className={`${baseWidthClass} ${baseHeightClass} bg-gray-800 rounded-b-lg mx-auto`} />
        </div>
    );
};

export default Monitor;
